import * as React from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { DataTable } from '@components/data-table/dataTable';
import { showDeviceStatusTooltip } from '@components/data-table/dataFormatters';
import { PaginationNavigation } from '@components/data-table/pagination/paginationNavigation';
import { SortDirection } from '@components/data-table/types';
import Card from '@components/card/card';
import { CloseButton } from '@components/controls/closeButton';
import { ExportButton } from '@components/controls/exportButton';
import { Loading } from '@components/loading/loading';
import { encode } from '@lib/base64';
import { useDeviceIdentifier } from '@lib/useDeviceIdentifier';
import { useTranslation } from '@lib/useTypedTranslation';
import { useWorldAction } from '@lib/useWorldAction';
import { useWorldRequest } from '@lib/useWorldRequest';

import { ClusterContext } from '../../../containers/core/maps/clusterContext';
import { useWorldRoutes } from '../../../routes/parts/allWorldRoutes';
import { exportClusterDevicesListAction, getClusterDevicesList } from '../../../services/core/maps';
import { isEmpty, isNil } from 'lodash';

export const DevicesCard = () => {
  const { t } = useTranslation(['maps', 'translation']);
  const deviceIdentifier = useDeviceIdentifier();
  const routes = useWorldRoutes();

  const { state, dispatch } = React.useContext(ClusterContext);
  const selected = !!state?.selectedCluster;

  const limit = 20;
  const [page, setPage] = React.useState(1);
  const [sortDirection, setSortDirection] = React.useState<SortDirection>();

  const getTableDataFetcher = React.useCallback(() => {
    if (!state?.selectedCluster) {
      return;
    }
    return getClusterDevicesList({
      boundingLatLong: state.selectedCluster,
      limit,
      offset: (page - 1) * limit,
      alertField: state.alertField,
      alertValues: state.alertValues,
      from: state.from,
      to: state.to,
      groups: state.groups,
      homeLocations: state.homeLocations,
      zoneId: state.zoneId,
      sort: sortDirection && {
        field: deviceIdentifier.id,
        order: sortDirection
      }
    });
  }, [page, deviceIdentifier.id, sortDirection, state]);
  const { data: tableData, loading: tableLoading } = useWorldRequest(getTableDataFetcher, { initialLoading: true });

  const exportClusterDevicesList = useWorldAction(exportClusterDevicesListAction);
  const onExport = React.useCallback(() => {
    return exportClusterDevicesList({
      boundingLatLong: state?.selectedCluster,
      limit: 1000,
      offset: 0,
      alertField: state?.alertField,
      alertValues: state?.alertValues,
      from: state?.from,
      to: state?.to,
      groups: state?.groups,
      homeLocations: state?.homeLocations,
      zoneId: state?.zoneId,
      sort: sortDirection && {
        field: deviceIdentifier.id,
        order: sortDirection
      }
    });
  }, [exportClusterDevicesList, deviceIdentifier.id, sortDirection, state]);

  const loading = tableLoading;

  return <Card className={classNames('devices-card', { selected })}>
    <div className={'devices-card_header'}>
      <h3>{t("DEVICES_HEADING", { ns: 'maps' })} {selected && tableData?.count && <>({tableData.count})</>}</h3>
      {selected && <div className="devices-card_header-buttons">
        <ExportButton
          className="devices-card_export"
          disabled={loading || !tableData?.list?.length}
          exportFunction={onExport}
          filename={`Devices.csv`}
        />
        <CloseButton
          handleClose={() => { dispatch({ type: 'SET_SELECTED_CLUSTER', payload: null }); }}
        />
      </div>}
    </div>
    {selected ?
      <div className="devices-card_main">
        <div className="devices-card_inner">
          {loading || !tableData ?
            <Loading isLoading transparentOverlay={false} />
            :
            <div className="devices-card_list">
              <DataTable
                sortedColumnIndex={0}
                sortDirection={sortDirection}
                onSort={(_, direction) => {
                  setPage(1);
                  setSortDirection(direction);
                }}
                columns={[
                  {
                    id: deviceIdentifier.id,
                    dataPath: deviceIdentifier.id,
                    processor: (pdi, { id }) => <Link to={`${routes.core.deviceOverview}?id=${encodeURIComponent(encode(id))}`}>
                      {(isEmpty(pdi) || isNil(pdi)) ? '-' : pdi}
                    </Link>,
                    sortable: true,
                    title: deviceIdentifier.label
                  },
                  {
                    id: state?.alertField || 'deviceStatus',
                    dataPath: state?.alertField || 'deviceStatus',
                    processor: (value, rowData) => showDeviceStatusTooltip(value, rowData, routes.core.deviceOverview),
                    title: t('STATUS', { ns: 'translation' }),
                    textAlign: 'center'
                  }
                ]}
                data={tableData.list}
              />
            </div>}
          {!!tableData && <PaginationNavigation
            limit={limit}
            offset={(page - 1) * limit}
            onNewPage={offset => setPage((offset / limit) + 1)}
            total={tableData.count}
            className='devices-card_pagination'
          />}
        </div>
      </div>
      :
      <p className="devices-card_prompt">{t("CLUSTER_PROMPT", { ns: 'maps' })}</p>}
  </Card>;
};
